import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Chip,
  Alert,
  LinearProgress,
  Switch,
  FormControlLabel,
  Grid,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import DataTable from '../components/DataTable';
import api from '../services/api';
import { useDateFormat } from '../hooks/useDateFormat';

interface Task {
  id: string;
  organizationId: string;
  accountId: string;
  name: string;
  description?: string;
  taskNumber?: string;
  isBillable: boolean;
  billingRate?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  organization?: {
    id: string;
    name: string;
    slug: string;
  };
  account?: {
    id: string;
    name: string;
  };
}

interface TaskFormData {
  organizationId: string;
  accountId: string;
  name: string;
  description: string;
  taskNumber: string;
  isBillable: boolean;
  billingRate: string;
  isActive: boolean;
}

export default function Tasks() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const { formatDateOnly } = useDateFormat();
  const [formData, setFormData] = useState<TaskFormData>({
    organizationId: '',
    accountId: '',
    name: '',
    description: '',
    taskNumber: '',
    isBillable: true,
    billingRate: '',
    isActive: true
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadTasks();
    loadAccounts();
    loadOrganizations();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getTasks();
      setTasks(response.data || []);
    } catch (err: any) {
      console.error('Error loading tasks:', err);
      setError(err.response?.data?.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await api.getAccounts();
      setAccounts(response.data || []);
    } catch (err: any) {
      console.error('Error loading accounts:', err);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await api.getOrganizations();
      setOrganizations(response.data || []);
    } catch (err: any) {
      console.error('Error loading organizations:', err);
    }
  };

  // Filter accounts based on selected organization
  const getFilteredAccounts = () => {
    if (!formData.organizationId) {
      return [];
    }
    return accounts.filter(account => account.organizationId === formData.organizationId);
  };

  // Handle organization change - reset account selection
  const handleOrganizationChange = (organizationId: string) => {
    setFormData({ 
      ...formData, 
      organizationId,
      accountId: '' // Reset account when organization changes
    });
  };

  const handleOpenDialog = (task?: Task) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        organizationId: task.organizationId,
        accountId: task.accountId,
        name: task.name,
        description: task.description || '',
        taskNumber: task.taskNumber || '',
        isBillable: task.isBillable,
        billingRate: task.billingRate?.toString() || '',
        isActive: task.isActive
      });
    } else {
      setEditingTask(null);
      setFormData({
        organizationId: '',
        accountId: '',
        name: '',
        description: '',
        taskNumber: '',
        isBillable: true,
        billingRate: '',
        isActive: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTask(null);
    setFormData({
      organizationId: '',
      accountId: '',
      name: '',
      description: '',
      taskNumber: '',
      isBillable: true,
      billingRate: '',
      isActive: true
    });
  };

  const handleSubmit = async () => {
    try {
      const submitData = {
        ...formData,
        billingRate: formData.billingRate ? parseFloat(formData.billingRate) : null
      };

      if (editingTask) {
        await api.updateTask(editingTask.id, submitData);
      } else {
        await api.createTask(submitData);
      }
      handleCloseDialog();
      loadTasks();
    } catch (err: any) {
      console.error('Error saving task:', err);
      setError(err.response?.data?.message || 'Failed to save task');
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await api.deleteTask(taskId);
        loadTasks();
      } catch (err: any) {
        console.error('Error deleting task:', err);
        setError(err.response?.data?.message || 'Failed to delete task');
      }
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && task.isActive) ||
                         (filterStatus === 'inactive' && !task.isActive);
    return matchesSearch && matchesStatus;
  });

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
    { 
      field: 'account', 
      headerName: 'Account', 
      width: 150,
      valueGetter: (params) => params.row.account?.name || 'N/A'
    },
    { field: 'taskNumber', headerName: 'Task Number', width: 150 },
    { 
      field: 'isBillable', 
      headerName: 'Billable', 
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value ? 'Yes' : 'No'} 
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    { 
      field: 'billingRate', 
      headerName: 'Billing Rate', 
      width: 120,
      valueFormatter: (params) => params.value ? `$${params.value}` : 'N/A'
    },
    { 
      field: 'isActive', 
      headerName: 'Status', 
      width: 100,
      renderCell: (params) => (
        <Chip 
          label={params.value ? 'Active' : 'Inactive'} 
          color={params.value ? 'success' : 'error'}
          size="small"
        />
      )
    },
    { 
      field: 'createdAt', 
      headerName: 'Created', 
      width: 120,
      valueFormatter: (params) => formatDateOnly(params.value)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 150,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<ViewIcon />}
          label="View"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteTask(params.row.id)}
        />
      ]
    }
  ];

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Tasks Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Task
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search tasks"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Filter by Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2" color="text.secondary">
                {filteredTasks.length} of {tasks.length} tasks
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tasks Table */}
      <DataTable
        rows={filteredTasks}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
      />

      {/* Add/Edit Task Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTask ? 'Edit Task' : 'Add New Task'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Organization</InputLabel>
                  <Select
                    value={formData.organizationId}
                    label="Organization"
                    onChange={(e) => handleOrganizationChange(e.target.value)}
                    disabled={!!editingTask}
                  >
                    {organizations.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Account</InputLabel>
                  <Select
                    value={formData.accountId}
                    label="Account"
                    onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                    disabled={!formData.organizationId}
                  >
                    {getFilteredAccounts().map((account) => (
                      <MenuItem key={account.id} value={account.id}>
                        {account.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Task Number"
                  value={formData.taskNumber}
                  onChange={(e) => setFormData({ ...formData, taskNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Billing Rate"
                  type="number"
                  value={formData.billingRate}
                  onChange={(e) => setFormData({ ...formData, billingRate: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isBillable}
                      onChange={(e) => setFormData({ ...formData, isBillable: e.target.checked })}
                    />
                  }
                  label="Billable"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingTask ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
